import { FORM_CONSTANTS } from './patientFormConfig'

function sanitizeDigits(value, maxLength) {
  const digitsOnly = `${value ?? ''}`.replace(/\D/g, '')
  if (!Number.isFinite(maxLength) || maxLength <= 0) {
    return digitsOnly
  }
  return digitsOnly.slice(0, maxLength)
}

function toIntegerOrNull(value) {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const numericValue = Number.parseInt(`${value}`, 10)
  return Number.isNaN(numericValue) ? null : numericValue
}

function getPreviousMonthDays(referenceDate) {
  return new Date(referenceDate.getFullYear(), referenceDate.getMonth(), 0).getDate()
}

export function getNormalizedAgeSecondaryUnit(unit) {
  return unit === FORM_CONSTANTS.AGE_SECONDARY_UNIT_DAYS
    ? FORM_CONSTANTS.AGE_SECONDARY_UNIT_DAYS
    : FORM_CONSTANTS.AGE_SECONDARY_UNIT_MONTHS
}

export function sanitizeAgeYearsInput(value) {
  return sanitizeDigits(value, FORM_CONSTANTS.AGE_INPUT_MAX_LENGTH)
}

export function sanitizeAgeSecondaryValueInput(value) {
  return sanitizeDigits(value, FORM_CONSTANTS.AGE_SECONDARY_INPUT_MAX_LENGTH)
}

export function buildPatientAgeFields({
  ageYears,
  ageSecondaryValue,
  ageSecondaryUnit,
} = {}) {
  const normalizedYears = sanitizeAgeYearsInput(ageYears)
  const normalizedSecondaryValue = sanitizeAgeSecondaryValueInput(ageSecondaryValue)
  const normalizedSecondaryUnit = getNormalizedAgeSecondaryUnit(ageSecondaryUnit)

  return {
    age: normalizedYears === '' ? null : toIntegerOrNull(normalizedYears),
    ageYears: normalizedYears,
    ageSecondaryValue: normalizedSecondaryValue,
    ageSecondaryUnit: normalizedSecondaryUnit,
  }
}

export function getPatientAgeInputSnapshot(patientDetails = {}) {
  const normalizedYears = sanitizeAgeYearsInput(
    patientDetails?.ageYears ?? patientDetails?.age ?? '',
  )
  const normalizedSecondaryValue = sanitizeAgeSecondaryValueInput(
    patientDetails?.ageSecondaryValue ?? '',
  )
  const normalizedSecondaryUnit = getNormalizedAgeSecondaryUnit(patientDetails?.ageSecondaryUnit)

  return {
    yearsText: normalizedYears,
    years: toIntegerOrNull(normalizedYears),
    secondaryValueText: normalizedSecondaryValue,
    secondaryValue: toIntegerOrNull(normalizedSecondaryValue),
    secondaryUnit: normalizedSecondaryUnit,
  }
}

export function getPatientAgeValidationMessage(patientDetails = {}) {
  const { yearsText, years, secondaryValueText, secondaryValue, secondaryUnit } =
    getPatientAgeInputSnapshot(patientDetails)

  if (yearsText === '') {
    return 'Age is required'
  }

  if (
    years === null ||
    years < FORM_CONSTANTS.MIN_AGE ||
    years > FORM_CONSTANTS.MAX_AGE
  ) {
    return `Years must be between ${FORM_CONSTANTS.MIN_AGE} and ${FORM_CONSTANTS.MAX_AGE}`
  }

  if (secondaryValueText === '') {
    return null
  }

  if (secondaryValue === null || secondaryValue < 0) {
    return secondaryUnit === FORM_CONSTANTS.AGE_SECONDARY_UNIT_DAYS
      ? `Days must be between ${FORM_CONSTANTS.MIN_AGE_DAYS} and ${FORM_CONSTANTS.MAX_AGE_DAYS}`
      : `Months must be between ${FORM_CONSTANTS.MIN_AGE_MONTHS} and ${FORM_CONSTANTS.MAX_AGE_MONTHS}`
  }

  if (
    secondaryUnit === FORM_CONSTANTS.AGE_SECONDARY_UNIT_MONTHS &&
    secondaryValue > FORM_CONSTANTS.MAX_AGE_MONTHS
  ) {
    return `Months must be between ${FORM_CONSTANTS.MIN_AGE_MONTHS} and ${FORM_CONSTANTS.MAX_AGE_MONTHS}`
  }

  if (
    secondaryUnit === FORM_CONSTANTS.AGE_SECONDARY_UNIT_DAYS &&
    secondaryValue > FORM_CONSTANTS.MAX_AGE_DAYS
  ) {
    return `Days must be between ${FORM_CONSTANTS.MIN_AGE_DAYS} and ${FORM_CONSTANTS.MAX_AGE_DAYS}`
  }

  if (years === FORM_CONSTANTS.MAX_AGE && secondaryValue > 0) {
    return `Years must be between ${FORM_CONSTANTS.MIN_AGE} and ${FORM_CONSTANTS.MAX_AGE}`
  }

  return null
}

export function hasValidPatientAgeInput(patientDetails = {}) {
  return !getPatientAgeValidationMessage(patientDetails)
}

export function buildDateOfBirthFromPatientAge(patientDetails = {}) {
  if (!hasValidPatientAgeInput(patientDetails)) {
    return null
  }

  const { years, secondaryValue, secondaryUnit } = getPatientAgeInputSnapshot(patientDetails)
  const dateOfBirth = new Date()
  dateOfBirth.setHours(0, 0, 0, 0)
  dateOfBirth.setFullYear(dateOfBirth.getFullYear() - (years || 0))

  if (secondaryValue) {
    if (secondaryUnit === FORM_CONSTANTS.AGE_SECONDARY_UNIT_DAYS) {
      dateOfBirth.setDate(dateOfBirth.getDate() - secondaryValue)
    } else {
      dateOfBirth.setMonth(dateOfBirth.getMonth() - secondaryValue)
    }
  }

  return `${dateOfBirth.toISOString().split('T')[0]} 00:00:00`
}

export function getDetailedAgeFromDateOfBirth(dateOfBirth) {
  if (!dateOfBirth) {
    return null
  }

  const birthDate = new Date(`${dateOfBirth}`.substring(0, 10))
  if (Number.isNaN(birthDate.getTime())) {
    return null
  }

  const today = new Date()
  let years = today.getFullYear() - birthDate.getFullYear()
  let months = today.getMonth() - birthDate.getMonth()
  let days = today.getDate() - birthDate.getDate()

  if (days < 0) {
    months -= 1
    days += getPreviousMonthDays(today)
  }

  if (months < 0) {
    years -= 1
    months += 12
  }

  return {
    years: Math.max(0, years),
    months: Math.max(0, months),
    days: Math.max(0, days),
  }
}

export function getPatientAgeFieldsFromPatientInfo(patientInfo = {}) {
  const detailedAge = getDetailedAgeFromDateOfBirth(patientInfo?.dateOfBirth)

  if (detailedAge) {
    let ageSecondaryValue = ''
    let ageSecondaryUnit = FORM_CONSTANTS.AGE_SECONDARY_UNIT_MONTHS

    if (detailedAge.years > 0) {
      if (detailedAge.months > 0) {
        ageSecondaryValue = `${detailedAge.months}`
        ageSecondaryUnit = FORM_CONSTANTS.AGE_SECONDARY_UNIT_MONTHS
      } else if (detailedAge.days > 0) {
        ageSecondaryValue = `${detailedAge.days}`
        ageSecondaryUnit = FORM_CONSTANTS.AGE_SECONDARY_UNIT_DAYS
      }
    } else if (detailedAge.months > 0) {
      ageSecondaryValue = `${detailedAge.months}`
      ageSecondaryUnit = FORM_CONSTANTS.AGE_SECONDARY_UNIT_MONTHS
    } else if (detailedAge.days > 0) {
      ageSecondaryValue = `${detailedAge.days}`
      ageSecondaryUnit = FORM_CONSTANTS.AGE_SECONDARY_UNIT_DAYS
    }

    return buildPatientAgeFields({
      ageYears: `${detailedAge.years}`,
      ageSecondaryValue,
      ageSecondaryUnit,
    })
  }

  return buildPatientAgeFields({
    ageYears: patientInfo?.age ?? '',
    ageSecondaryValue: patientInfo?.ageSecondaryValue ?? '',
    ageSecondaryUnit:
      patientInfo?.ageSecondaryUnit ?? FORM_CONSTANTS.DEFAULT_AGE_SECONDARY_UNIT,
  })
}

export function formatPatientAgeSummary(patientDetails = {}) {
  const { years, secondaryValue, secondaryUnit } = getPatientAgeInputSnapshot(patientDetails)

  if (years === null) {
    return null
  }

  const parts = [`${years}y`]
  if (secondaryValue !== null && secondaryValue > 0) {
    parts.push(
      secondaryUnit === FORM_CONSTANTS.AGE_SECONDARY_UNIT_DAYS
        ? `${secondaryValue}d`
        : `${secondaryValue}m`,
    )
  }

  return parts.join(' ')
}
